import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { OptionType } from 'components/select/MultiSelectCheckbox';

import { FilterConfig, SelectedFilters } from 'components/filter-panel/type';

import { useCollapse } from 'hooks/useCollapse';
import { debounce, isEmpty, isEqual, pull, uniq } from 'lodash';

export const useFilterPanel = (
  items: FilterConfig[],
  onFilterChange: (filters: SelectedFilters) => void,
  onSearch: ((filterKey: string, searchTerm: string) => void) | undefined,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});

  const debouncedSearchRef = useRef(
    debounce((key: string, term: string, callback?: (key: string, t: string) => void) => {
      if (callback) callback(key, term);
    }, 300),
  );

  const { openCollapseKeys, toggleCollapse, setOpenCollapseKeys } = useCollapse(
    items,
    selectedFilters,
  );

  useEffect(() => {
    const initialOpenCollapseKeys = items.reduce(
      (acc, item) => {
        acc[item.key] = true;

        return acc;
      },
      {} as Record<string, boolean>,
    );

    setOpenCollapseKeys(initialOpenCollapseKeys);
  }, []);

  // Get filters from URL and set to state
  useEffect(() => {
    const params: Record<string, string[]> = {};

    searchParams.forEach((value, key) => {
      const valuesArray = value.split(',');

      // Only update if the values are different from the current state
      if (!isEqual(JSON.stringify(valuesArray), JSON.stringify(selectedFilters[key])))
        params[key] = valuesArray;
    });

    if (!isEmpty(Object.keys(params))) setSelectedFilters(params);
  }, [searchParams]);

  // Update URL when filters change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (!isEmpty(value)) newSearchParams.set(key, value.join(','));
    });

    // Only update if the values are different from the current URL
    if (newSearchParams.toString() !== searchParams.toString())
      setSearchParams(newSearchParams, { replace: true });
  }, [selectedFilters, setSearchParams]);

  useEffect(() => {
    onFilterChange(selectedFilters);
  }, [selectedFilters]);

  const updateFilters = (prev: SelectedFilters, key: string, newValues: string[]) => {
    const updatedFilters = { ...prev };

    if (isEmpty(newValues)) {
      delete updatedFilters[key];

      return updatedFilters;
    }

    updatedFilters[key] = newValues;

    return updatedFilters;
  };

  const handleCheckboxSelect = useCallback(
    (key: string, value: string) => (event: FormEvent<HTMLDivElement>) => {
      event.stopPropagation();

      setSelectedFilters((prev) => {
        const currentValues = prev[key] || [];
        const isSelected = currentValues.includes(value);

        const newValues = isSelected
          ? pull([...currentValues], value)
          : uniq([...currentValues, value]);

        return updateFilters(prev, key, newValues);
      });
    },
    [updateFilters],
  );

  const handleMultiSelectChange = useCallback(
    (key: string) => (selected: OptionType[]) => {
      const newValues = selected.map((opt) => opt.value);

      setSelectedFilters((prev) => updateFilters(prev, key, newValues));
    },
    [updateFilters],
  );

  const handleSearchChange = (key: string) => (value: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (onSearch) {
      debouncedSearchRef.current(key, value, onSearch);
    }
  };

  return {
    searchTerms,
    toggleCollapse,
    selectedFilters,
    openCollapseKeys,

    handleSearchChange,
    handleCheckboxSelect,
    handleMultiSelectChange,
  };
};
